"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.checkTypebox = checkTypebox;
exports.getValueShape = getValueShape;
const lodash_1 = __importDefault(require("lodash"));
const ajv_1 = __importDefault(require("ajv"));
const ajv_formats_1 = __importDefault(require("ajv-formats"));
const Log_1 = require("./Log");
/**
 * Internal helpers for checkTypebox. Here we memoize the constructed AJV and
 * the compiled schema. Reconstructing and recompiling on each request will
 * completely tank a server's performance.
 *
 * A possible issue is if the schemas passed to this function can vary, meaning
 * the cache of them bloats as the server runs. We assume that there is a
 * finite list of schemas that will ever go through this function in a given
 * server run, so this is left unaddressed.
 */
// Memoized ajv instance.
let ajv;
// Memoized compile results. Maps schema objects to result of ajv.compile
// (validate function).
const ajvCompiledSchemas = new Map();
// Returns the result of ajv.compile(schema), with memoization.
function getTypeboxValidator(schema) {
    if (!ajv) {
        ajv = (0, ajv_formats_1.default)(new ajv_1.default(), [
            "date-time",
            "email",
            "uuid",
            "ipv4",
            "ipv6",
            "double",
        ])
            .addKeyword("kind")
            .addKeyword("modifier")
            .addKeyword({
            keyword: "customObjectType",
            type: "object",
            validate: function (name, val) {
                if (name === "dateT") {
                    // Validate that the given object is a valid Date object.
                    return val instanceof Date && !isNaN(val.valueOf());
                }
                // Unknown object type? Assume it's invalid.
                return false;
            },
        });
    }
    let validate = ajvCompiledSchemas.get(schema);
    if (!validate) {
        validate = ajv.compile(schema);
        ajvCompiledSchemas.set(schema, validate);
    }
    return validate;
}
/**
 * Given a Typebox schema and a value of unknown type, verify the unknown value
 * matches the schema. Return the value unchanged if it matches, which
 * Typescript now accepts is being the right type. Otherwise, throw an error.
 *
 * Optional parameters:
 * - errorPrefix: Include this string in front of the thrown error message.
 * - log: If passed, calls log.debug with the full details of a Type mismatch.
 * - logOnFail: If passed, logs the checkTypebox error at the debug level.
 */
function checkTypebox(opts) {
    const { schema, value, logOnFail } = opts;
    let { log, errorPrefix } = opts;
    if (!errorPrefix) {
        errorPrefix = "";
    }
    log = log || (0, Log_1.createNoopLogApi)();
    const validate = getTypeboxValidator(schema);
    const valid = validate(value);
    if (!valid) {
        const fullMessage = validate.errors
            ? validate.errors
                .map((e) => (e.dataPath || e.instancePath || "") + " " + e.message)
                .join("; \n")
            : "Unknown AJV error";
        if (logOnFail) {
            log.debug({
                errorPrefix,
                errorRaw: validate.errors,
                errorMessage: fullMessage,
                schema,
                valueShape: getValueShape(value),
            }, "checkTypebox mismatch details");
        }
        throw new Error(errorPrefix + ": Runtime type-check failed \n" + fullMessage);
    }
    else {
        return value;
    }
}
/**
 * Takes a JSON-like value and outputs its structure, replacing all primitive
 * values with their type. Keys are not mangled.
 */
function getValueShape(value) {
    if (lodash_1.default.isFunction(value)) {
        return "function";
    }
    else if (lodash_1.default.isObject(value)) {
        // This is identical to _.mapValues, but maintains type safety.
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, getValueShape(v)]));
    }
    else if (lodash_1.default.isArray(value)) {
        return value.map(getValueShape);
    }
    else if (value === null) {
        return "null";
    }
    else if (typeof value === "undefined") {
        return "undefined";
    }
    else if (lodash_1.default.isNaN(value)) {
        return "NaN";
    }
    else if (lodash_1.default.isNumber(value)) {
        return "number";
    }
    else if (lodash_1.default.isBoolean(value)) {
        return "boolean";
    }
    else if (lodash_1.default.isString(value)) {
        return "string";
    }
    else if (lodash_1.default.isSymbol(value)) {
        return "symbol";
    }
    else {
        return "unknown";
    }
}
//# sourceMappingURL=TypeCheck.js.map